import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { HelpFooter } from '@/packages/courses/pages/registration/components/help-footer';
import { BaseDialog } from '@/components/shared/base-dialog';
import { Icon } from '@/components/shared/icon';
import { useNotification } from '@/context/notification';
import type { InstitutionSubscriptionConfig } from '@/data/catalog/offer/interfaces';

interface InstructionDialogProps {
  onClose: () => void;
  onLinkClick?: () => void;
  institutionName?: string;
  config: InstitutionSubscriptionConfig;
}

const stripProtocol = (url: string) => url.replace(/^https?:\/\//, '');

export const InstructionDialog = ({
  onClose,
  onLinkClick,
  institutionName = '',
  config,
}: InstructionDialogProps) => {
  const { toast } = useNotification();
  const { link, coupon, external_purchase_action } = config;

  const handleCopyCoupon = () => {
    if (!coupon) return;

    navigator.clipboard.writeText(coupon);
    toast({
      title: 'Cupom copiado',
      content: 'O cupom foi copiado para a área de transferência.',
      type: 'success',
    });
  };

  return (
    <BaseDialog
      open
      onClose={onClose}
      icon={<Icon name="external-link" size={48} alt="Link externo" />}
      title={`Complete sua matrícula na instituição ${institutionName}`}
    >
      <Stack spacing={2}>
        <Typography>
          Para garantir seu benefício Galena, você deve completar esses passos
          no site da instituição parceira:
        </Typography>

        <Stack spacing={3}>
          <Stack
            spacing={2}
            bgcolor="neutral.100"
            px={2}
            py={1}
            borderRadius={({ shape }) => `${shape.borderRadius}px`}
          >
            <Box>
              <Typography color="text.secondary">Acesse o link:</Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  wordBreak: 'break-all',
                }}
              >
                <Link
                  href={link}
                  target="_blank"
                  rel="noopener"
                  onClick={onLinkClick}
                >
                  {stripProtocol(link)}
                </Link>
                <OpenInNewIcon sx={{ fontSize: 16, alignSelf: 'flex-start' }} />
              </Box>
            </Box>

            {external_purchase_action && (
              <Typography>
                Clique em <strong>Comprar agora</strong>
              </Typography>
            )}

            {coupon && (
              <Box>
                <Typography color="text.secondary">Utilize o cupom:</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h1">{coupon}</Typography>
                  <IconButton
                    onClick={handleCopyCoupon}
                    aria-label="Copiar cupom"
                    size="small"
                  >
                    <ContentCopyIcon
                      fontSize="small"
                      sx={{ color: 'common.black' }}
                    />
                  </IconButton>
                </Stack>
              </Box>
            )}

            {coupon ? (
              <Typography>
                Clique em aplicar, confirme que o desconto foi considerado
                corretamente e finalize seu cadastro.
              </Typography>
            ) : (
              <Typography>
                <strong>Importante:</strong> a conclusão da matrícula deve ser
                realizada diretamente no ambiente da instituição parceira para
                garantir o correto direcionamento da sua inscrição.
              </Typography>
            )}
          </Stack>

          <Button
            variant="contained"
            fullWidth
            endIcon={<ArrowRightAltIcon />}
            href={link}
            target="_blank"
            rel="noopener"
            onClick={onLinkClick}
          >
            Ir para o site da instituição
          </Button>
        </Stack>

        <HelpFooter />
      </Stack>
    </BaseDialog>
  );
};
